// ── User Permission Matrix ──
// 3-pane permission management: user list | detail + matrix | why drawer

import { useState, useMemo, useCallback } from "react";
import { useControlPlane } from "@/data/controlPlane/context";
import { CAPABILITY_REGISTRY, getAllCategories } from "@/data/controlPlane/registry";
import type {
  ResolvedCapability, BehaviorMode, ManagedUser, ChangeRequest,
  DecisionSummary, UserAccountStatus,
} from "@/data/controlPlane/types";
import type { UserRole } from "@/data/RoleContext";
import { ROLES } from "@/data/RoleContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Separator } from "@/components/ui/separator";
import {
  Shield, Eye, EyeOff, Lock, Activity, Search, Info,
  ChevronDown, ChevronRight, CheckCircle, XCircle, Ban,
  ShieldX, Mail, Clock, RotateCcw, ShieldCheck, ShieldAlert,
  Zap, ArrowRight, Building2, KeyRound, Layers, UserCog,
  AlertTriangle, Eraser, EyeIcon, FlaskConical,
} from "lucide-react";

// ── Constants ──

const MODE_COLORS: Record<BehaviorMode, string> = {
  active: "bg-success/10 text-success border-success/20",
  hidden: "bg-muted text-muted-foreground border-border",
  read_only: "bg-warning/10 text-warning border-warning/20",
  shadow: "bg-primary/10 text-primary border-primary/20",
  internal_only: "bg-accent text-accent-foreground border-accent",
};

const USER_STATUS_CONFIG: Record<UserAccountStatus, { icon: typeof CheckCircle; color: string; label: string }> = {
  active: { icon: CheckCircle, color: "text-success", label: "Active" },
  inactive: { icon: XCircle, color: "text-muted-foreground", label: "Inactive" },
  suspended: { icon: Ban, color: "text-destructive", label: "Suspended" },
  invited: { icon: Mail, color: "text-primary", label: "Invited" },
  deactivated: { icon: ShieldX, color: "text-muted-foreground", label: "Deactivated" },
};

const CATEGORY_LABELS: Record<string, string> = {
  scheduling: "Scheduling", finance: "Finance", crm: "CRM", qa: "Quality Assurance",
  materials: "Materials", admin: "Administration", sales: "Sales", operations: "Operations",
  warehouse: "Warehouse", intelligence: "Intelligence", portal: "Portals",
  compliance: "Compliance", reporting: "Reporting", communications: "Communications",
};

const CATEGORY_ICONS: Record<string, typeof Shield> = {
  scheduling: Clock, finance: Building2, crm: UserCog, qa: ShieldCheck,
  materials: Layers, admin: Shield, sales: Zap, operations: Activity,
  warehouse: Layers, intelligence: Eye, portal: EyeIcon,
  compliance: ShieldAlert, reporting: Layers, communications: Mail,
};

function timeAgo(iso?: string): string {
  if (!iso) return "Never";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ── Source Badge ──
function SourceBadge({ source }: { source: "role" | "tenant" | "user" | "none" }) {
  if (source === "none") return <span className="text-[10px] text-muted-foreground/30">—</span>;
  const styles = {
    role: "bg-muted/30 text-muted-foreground border-border/50",
    tenant: "bg-primary/6 text-primary/80 border-primary/15",
    user: "bg-warning/8 text-warning border-warning/15",
  };
  const labels = { role: "Role", tenant: "Tenant", user: "User" };
  return <Badge variant="outline" className={`text-[9px] font-normal ${styles[source]}`}>{labels[source]}</Badge>;
}

// ── Override source detection ──
function getOverrideSource(capId: string, user: ManagedUser, tenantOverrides: Record<string, { enabled: boolean; behaviorMode: BehaviorMode }>) {
  const hasUser = capId in user.capabilityOverrides;
  const hasTenant = capId in tenantOverrides;
  return { hasUser, hasTenant };
}

export default function UserPermissionMatrix() {
  const cp = useControlPlane();
  const {
    users, tenants, roles, resolved, explain,
    resolveForUser, updateUserStatus, updateUserRole,
    setUserCapabilityOverride, clearUserCapabilityOverride,
    simulate,
  } = cp;

  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [userSearch, setUserSearch] = useState("");
  const [userStatusFilter, setUserStatusFilter] = useState<string>("all");
  const [whyCapId, setWhyCapId] = useState<string | null>(null);
  const [userDetailTab, setUserDetailTab] = useState<"permissions" | "access">("permissions");
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [pendingEdit, setPendingEdit] = useState<{ capId: string; mode: BehaviorMode } | null>(null);

  const categories = useMemo(() => getAllCategories(), []);

  const filteredUsers = useMemo(() => {
    return users.filter(u => {
      const matchSearch = !userSearch ||
        u.displayName.toLowerCase().includes(userSearch.toLowerCase()) ||
        u.email.toLowerCase().includes(userSearch.toLowerCase());
      const matchStatus = userStatusFilter === "all" || u.status === userStatusFilter;
      return matchSearch && matchStatus;
    });
  }, [users, userSearch, userStatusFilter]);

  const selectedUser = selectedUserId ? users.find(u => u.id === selectedUserId) : null;
  const selectedUserResolved = selectedUserId ? resolveForUser(selectedUserId) : [];
  const selectedTenant = selectedUser ? tenants.find(t => t.id === selectedUser.tenantId) : null;
  const tenantOverrides = selectedTenant?.capabilityOverrides ?? {};

  const whyData = useMemo(() => {
    if (!whyCapId || !selectedUserId) return null;
    const cap = selectedUserResolved.find(r => r.id === whyCapId);
    if (!cap) return null;
    return {
      summary: explain(whyCapId),
      cap,
      userOverride: selectedUser?.capabilityOverrides[whyCapId],
      tenantOverride: tenantOverrides[whyCapId],
    };
  }, [whyCapId, selectedUserId, selectedUserResolved, explain, selectedUser, tenantOverrides]);

  const categoryGroups = useMemo(() => {
    return categories.map(cat => {
      const caps = CAPABILITY_REGISTRY.filter(c => c.category === cat);
      const resolvedCaps = caps.map(c => {
        const r = selectedUserResolved.find(rc => rc.id === c.id);
        const { hasUser, hasTenant } = selectedUser
          ? getOverrideSource(c.id, selectedUser, tenantOverrides)
          : { hasUser: false, hasTenant: false };
        return { def: c, resolved: r, hasUserOverride: hasUser, hasTenantOverride: hasTenant };
      });
      const activeCount = resolvedCaps.filter(c => c.resolved?.enabled && c.resolved?.accessible).length;
      const overriddenCount = resolvedCaps.filter(c => c.hasUserOverride || c.hasTenantOverride).length;
      return { category: cat, caps: resolvedCaps, activeCount, overriddenCount, total: caps.length };
    }).filter(g => g.caps.length > 0);
  }, [categories, selectedUserResolved, selectedUser, tenantOverrides]);

  const toggleCategory = useCallback((cat: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return next;
    });
  }, []);

  const effectiveAccess = useMemo(() => {
    if (!selectedUserId) return { modules: [] as string[], routes: [] as string[], capCount: 0, totalCount: 0 };
    const active = selectedUserResolved.filter(r => r.enabled && r.accessible && r.effectiveMode !== "hidden");
    const moduleSet = new Set(active.map(r => r.category));
    const routeSet = new Set<string>();
    active.forEach(r => {
      const def = CAPABILITY_REGISTRY.find(c => c.id === r.id);
      def?.uiSurfaces.forEach(s => routeSet.add(s));
    });
    return {
      modules: [...moduleSet],
      routes: [...routeSet],
      capCount: active.length,
      totalCount: selectedUserResolved.length,
    };
  }, [selectedUserId, selectedUserResolved]);

  const bulkSetCategory = useCallback((cat: string, mode: BehaviorMode) => {
    if (!selectedUser) return;
    const caps = CAPABILITY_REGISTRY.filter(c => c.category === cat);
    caps.forEach(c => {
      const enabled = mode !== "hidden";
      setUserCapabilityOverride(selectedUser.id, c.id, enabled, mode, `Bulk: set ${cat} to ${mode}`);
    });
  }, [selectedUser, setUserCapabilityOverride]);

  const bulkClearCategory = useCallback((cat: string) => {
    if (!selectedUser) return;
    const caps = CAPABILITY_REGISTRY.filter(c => c.category === cat);
    caps.forEach(c => {
      clearUserCapabilityOverride(selectedUser.id, c.id);
    });
  }, [selectedUser, clearUserCapabilityOverride]);

  const requestChange = useCallback((change: ChangeRequest) => {
    if (change.type === "user_role_change") {
      updateUserRole(change.targetId, change.payload.role as UserRole, "Changed via Permission Matrix");
    } else if (change.type === "user_status_change") {
      updateUserStatus(change.targetId, change.payload.status as UserAccountStatus, "Changed via Permission Matrix");
    }
  }, [updateUserRole, updateUserStatus]);

  return (
    <div className="flex gap-0 h-[calc(100vh-260px)] min-h-[500px] rounded-xl border border-border overflow-hidden bg-card/30">
      {/* ═══ LEFT PANE: User List ═══ */}
      <div className="w-[260px] shrink-0 border-r border-border/50 flex flex-col bg-card/40">
        <div className="p-3 space-y-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground/40" />
            <Input placeholder="Search users..." value={userSearch}
              onChange={e => setUserSearch(e.target.value)}
              className="pl-8 h-8 text-[11px] bg-background/50 border-border/50 focus:border-primary/30" />
          </div>
          <Select value={userStatusFilter} onValueChange={setUserStatusFilter}>
            <SelectTrigger className="h-7 text-[11px] bg-background/50 border-border/50"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-[11px]">All Statuses</SelectItem>
              {(["active", "inactive", "suspended", "invited", "deactivated"] as UserAccountStatus[]).map(s => (
                <SelectItem key={s} value={s} className="text-[11px] capitalize">{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Separator className="bg-border/30" />
        <ScrollArea className="flex-1">
          <div className="py-1">
            {filteredUsers.map(u => {
              const sc = USER_STATUS_CONFIG[u.status];
              const StatusIcon = sc.icon;
              const isSelected = selectedUserId === u.id;
              const overrideCount = Object.keys(u.capabilityOverrides).length;
              return (
                <button key={u.id} onClick={() => setSelectedUserId(isSelected ? null : u.id)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-all ${
                    isSelected
                      ? "bg-primary/6 border-l-2 border-l-primary"
                      : "hover:bg-muted/15 border-l-2 border-l-transparent"
                  }`}>
                  <div className={`h-7 w-7 rounded-full flex items-center justify-center text-[9px] font-semibold shrink-0 ${
                    isSelected ? "bg-primary/15 text-primary" : "bg-muted/30 text-muted-foreground"
                  }`}>
                    {u.firstName[0]}{u.lastName[0]}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={`text-[11px] font-medium truncate ${isSelected ? "text-foreground" : "text-foreground/80"}`}>{u.displayName}</p>
                    <div className="flex items-center gap-1.5">
                      <StatusIcon className={`h-2 w-2 ${sc.color}`} />
                      <span className="text-[10px] text-muted-foreground/60 capitalize">{u.role.replace("_", " ")}</span>
                    </div>
                  </div>
                  {overrideCount > 0 && (
                    <span className="text-[9px] font-semibold text-warning bg-warning/8 rounded-full h-5 w-5 flex items-center justify-center shrink-0">
                      {overrideCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </ScrollArea>
      </div>

      {/* ═══ CENTER PANE: User Detail + Permission Matrix ═══ */}
      <div className="flex-1 min-w-0 flex flex-col">
        {!selectedUser ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center space-y-3">
              <div className="h-12 w-12 mx-auto rounded-xl bg-muted/20 flex items-center justify-center">
                <UserCog className="h-6 w-6 text-muted-foreground/30" />
              </div>
              <p className="text-[12px] text-muted-foreground/50">Select a user to manage permissions</p>
            </div>
          </div>
        ) : (
          <>
            {/* User header */}
            <div className="px-5 py-4 border-b border-border/40">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center text-[11px] font-bold text-primary">
                    {selectedUser.firstName[0]}{selectedUser.lastName[0]}
                  </div>
                  <div>
                    <h3 className="text-[13px] font-bold text-foreground">{selectedUser.displayName}</h3>
                    <p className="text-[11px] text-muted-foreground/60">{selectedUser.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={selectedUser.role} onValueChange={(v) =>
                    requestChange({ type: "user_role_change", targetId: selectedUser.id, payload: { role: v } })
                  }>
                    <SelectTrigger className="h-7 w-[130px] text-[11px] bg-background/50"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ROLES.map(r => (
                        <SelectItem key={r.key} value={r.key} className="text-[11px]">{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Badge variant="outline" className={`text-[10px] font-normal ${USER_STATUS_CONFIG[selectedUser.status].color}`}>
                    {USER_STATUS_CONFIG[selectedUser.status].label}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <Tabs value={userDetailTab} onValueChange={(v) => setUserDetailTab(v as typeof userDetailTab)} className="flex-1 flex flex-col min-h-0">
              <div className="px-5 pt-3 border-b border-border/30">
                <TabsList className="h-8 bg-muted/20 p-0.5 rounded-md">
                  <TabsTrigger value="permissions" className="text-[11px] gap-1 rounded-sm data-[state=active]:bg-background data-[state=active]:shadow-sm">
                    <KeyRound className="h-3 w-3" /> Permissions
                  </TabsTrigger>
                  <TabsTrigger value="access" className="text-[11px] gap-1 rounded-sm data-[state=active]:bg-background data-[state=active]:shadow-sm">
                    <EyeIcon className="h-3 w-3" /> View As User
                  </TabsTrigger>
                </TabsList>
              </div>

              {/* ── Permissions Tab ── */}
              <TabsContent value="permissions" className="flex-1 min-h-0 mt-0">
                <ScrollArea className="h-full">
                  <div className="p-5 space-y-1">
                    {categoryGroups.map(group => {
                      const isExpanded = expandedCategories.has(group.category);
                      const CatIcon = CATEGORY_ICONS[group.category] ?? Layers;
                      return (
                        <Collapsible key={group.category} open={isExpanded} onOpenChange={() => toggleCategory(group.category)}>
                          <CollapsibleTrigger className="w-full">
                            <div className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg transition-colors ${
                              isExpanded ? "bg-muted/15" : "hover:bg-muted/10"
                            }`}>
                              <ChevronRight className={`h-3.5 w-3.5 text-muted-foreground/40 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                              <CatIcon className="h-3.5 w-3.5 text-primary/60" />
                              <span className="text-[12px] font-semibold text-foreground flex-1 text-left">
                                {CATEGORY_LABELS[group.category] ?? group.category}
                              </span>
                              <span className="text-[10px] text-muted-foreground/50 tabular-nums">
                                {group.activeCount}/{group.total}
                              </span>
                              {group.overriddenCount > 0 && (
                                <span className="text-[9px] font-semibold text-warning bg-warning/8 rounded-full h-4 min-w-[16px] px-1 flex items-center justify-center">
                                  {group.overriddenCount}
                                </span>
                              )}
                            </div>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            {/* Column headers */}
                            <div className="grid grid-cols-[1fr_72px_72px_72px_76px_28px] gap-1 px-3 py-2 text-[9px] font-semibold text-muted-foreground/50 uppercase tracking-wider ml-7">
                              <span>Capability</span>
                              <span className="text-center">Role</span>
                              <span className="text-center">Tenant</span>
                              <span className="text-center">User</span>
                              <span className="text-center">Final</span>
                              <span></span>
                            </div>
                            <div className="ml-7">
                              {group.caps.map(({ def, resolved: r, hasUserOverride, hasTenantOverride }) => {
                                const finalMode = r?.effectiveMode ?? "hidden";
                                const isEnabled = r?.enabled && r?.accessible;
                                const roleDef = def.rolesAllowed.includes(selectedUser.role);
                                return (
                                  <div key={def.id} className="grid grid-cols-[1fr_72px_72px_72px_76px_28px] gap-1 py-2 px-3 items-center hover:bg-muted/8 transition-colors rounded-md group">
                                    <div className="min-w-0">
                                      <p className="text-[11px] font-medium text-foreground/80 truncate">{def.label}</p>
                                    </div>
                                    {/* Role */}
                                    <div className="flex justify-center">
                                      <Badge variant="outline" className={`text-[8px] font-normal ${
                                        roleDef
                                          ? "bg-muted/20 text-muted-foreground/60 border-border/30"
                                          : "bg-muted/10 text-muted-foreground/25 border-border/20"
                                      }`}>
                                        {roleDef ? (def.behaviorMode === "active" ? "active" : def.behaviorMode.replace("_", " ")) : "denied"}
                                      </Badge>
                                    </div>
                                    {/* Tenant */}
                                    <div className="flex justify-center">
                                      {hasTenantOverride ? (
                                        <Badge variant="outline" className="text-[8px] bg-primary/6 text-primary/70 border-primary/15 font-normal">
                                          {tenantOverrides[def.id]?.behaviorMode.replace("_", " ") ?? "set"}
                                        </Badge>
                                      ) : (
                                        <span className="text-[10px] text-muted-foreground/20">—</span>
                                      )}
                                    </div>
                                    {/* User */}
                                    <div className="flex justify-center">
                                      {hasUserOverride ? (
                                        <Badge variant="outline" className="text-[8px] bg-warning/8 text-warning/80 border-warning/15 font-semibold">
                                          {selectedUser.capabilityOverrides[def.id]?.behaviorMode.replace("_", " ") ?? "set"}
                                        </Badge>
                                      ) : (
                                        <span className="text-[10px] text-muted-foreground/20">—</span>
                                      )}
                                    </div>
                                    {/* Final */}
                                    <div className="flex justify-center">
                                      <Select
                                        value={finalMode}
                                        onValueChange={(v) => {
                                          const mode = v as BehaviorMode;
                                          const enabled = mode !== "hidden";
                                          setUserCapabilityOverride(selectedUser.id, def.id, enabled, mode, `Set to ${mode} via Permission Matrix`);
                                        }}
                                      >
                                        <SelectTrigger className={`h-6 w-[68px] text-[9px] border font-medium ${MODE_COLORS[finalMode]}`}>
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {(["active", "read_only", "hidden", "shadow", "internal_only"] as BehaviorMode[]).map(m => (
                                            <SelectItem key={m} value={m} className="text-[10px] capitalize">{m.replace("_", " ")}</SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    {/* Why */}
                                    <button onClick={() => setWhyCapId(def.id)}
                                      className="text-muted-foreground/30 hover:text-primary transition-colors"
                                      title="Why is this the current state?">
                                      <Info className="h-3.5 w-3.5" />
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                            {/* Bulk actions */}
                            <div className="flex items-center gap-1 ml-7 px-3 py-2">
                              <span className="text-[9px] text-muted-foreground/40 uppercase tracking-wider mr-1">Bulk:</span>
                              <Button size="sm" variant="ghost" className="h-5 text-[9px] px-1.5 text-muted-foreground/40 hover:text-foreground"
                                onClick={() => bulkSetCategory(group.category, "read_only")}>
                                <Lock className="h-2.5 w-2.5 mr-0.5" /> Read Only
                              </Button>
                              <Button size="sm" variant="ghost" className="h-5 text-[9px] px-1.5 text-muted-foreground/40 hover:text-foreground"
                                onClick={() => bulkSetCategory(group.category, "hidden")}>
                                <EyeOff className="h-2.5 w-2.5 mr-0.5" /> Hide All
                              </Button>
                              <Button size="sm" variant="ghost" className="h-5 text-[9px] px-1.5 text-muted-foreground/40 hover:text-foreground"
                                onClick={() => bulkClearCategory(group.category)}>
                                <Eraser className="h-2.5 w-2.5 mr-0.5" /> Clear
                              </Button>
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      );
                    })}
                  </div>
                </ScrollArea>
              </TabsContent>

              {/* ── View As User Tab ── */}
              <TabsContent value="access" className="flex-1 min-h-0 mt-0">
                <ScrollArea className="h-full">
                  <div className="p-5 space-y-5">
                    <div className="rounded-xl bg-muted/10 border border-border/30 p-4 space-y-3">
                      <h4 className="text-[12px] font-semibold text-foreground flex items-center gap-1.5">
                        <EyeIcon className="h-3.5 w-3.5 text-primary/60" />
                        What {selectedUser.firstName} sees right now
                      </h4>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="text-center p-3 rounded-lg bg-background/50 border border-border/30">
                          <p className="text-lg font-bold text-foreground">{effectiveAccess.capCount}</p>
                          <p className="text-[10px] text-muted-foreground/50">Capabilities</p>
                        </div>
                        <div className="text-center p-3 rounded-lg bg-background/50 border border-border/30">
                          <p className="text-lg font-bold text-foreground">{effectiveAccess.modules.length}</p>
                          <p className="text-[10px] text-muted-foreground/50">Modules</p>
                        </div>
                        <div className="text-center p-3 rounded-lg bg-background/50 border border-border/30">
                          <p className="text-lg font-bold text-foreground">{effectiveAccess.routes.length}</p>
                          <p className="text-[10px] text-muted-foreground/50">Routes</p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <p className="text-[10px] text-muted-foreground/50 uppercase tracking-wider mb-2">Modules Available</p>
                      <div className="flex flex-wrap gap-1.5">
                        {effectiveAccess.modules.map(m => (
                          <Badge key={m} variant="outline" className="text-[10px] bg-success/5 text-success/70 border-success/15 capitalize font-normal">
                            {CATEGORY_LABELS[m] ?? m}
                          </Badge>
                        ))}
                        {categories.filter(c => !(effectiveAccess.modules as string[]).includes(c)).map(m => (
                          <Badge key={m} variant="outline" className="text-[10px] bg-muted/10 text-muted-foreground/25 border-border/20 capitalize font-normal">
                            {CATEGORY_LABELS[m] ?? m}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    <div>
                      <p className="text-[10px] text-muted-foreground/50 uppercase tracking-wider mb-2">Routes Accessible</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1">
                        {effectiveAccess.routes.map(r => (
                          <div key={r} className="flex items-center gap-1.5 text-[11px] p-1.5 rounded-md bg-success/5">
                            <CheckCircle className="h-3 w-3 text-success/50 shrink-0" />
                            <span className="font-mono text-foreground/70 truncate">{r}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <p className="text-[10px] text-muted-foreground/50 uppercase tracking-wider mb-2">
                        Capabilities ({effectiveAccess.capCount} of {effectiveAccess.totalCount})
                      </p>
                      <div className="grid grid-cols-2 gap-1 max-h-[300px] overflow-y-auto">
                        {selectedUserResolved.map(r => (
                          <div key={r.id} className={`flex items-center gap-1.5 text-[11px] p-1.5 rounded-md ${
                            r.enabled && r.accessible && r.effectiveMode !== "hidden"
                              ? "bg-success/5" : "bg-muted/5 opacity-30"
                          }`}>
                            {r.enabled && r.accessible && r.effectiveMode !== "hidden"
                              ? <CheckCircle className="h-3 w-3 text-success/50 shrink-0" />
                              : <XCircle className="h-3 w-3 text-muted-foreground/30 shrink-0" />
                            }
                            <span className="truncate text-foreground/70">{r.label}</span>
                            {r.effectiveMode === "read_only" && (
                              <Lock className="h-2.5 w-2.5 text-warning/50 shrink-0 ml-auto" />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </ScrollArea>
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>

      {/* ═══ RIGHT PANE: Why Drawer ═══ */}
      <Sheet open={!!whyCapId} onOpenChange={() => setWhyCapId(null)}>
        <SheetContent side="right" className="w-[380px] sm:w-[400px] border-l border-border/30">
          <SheetHeader className="pb-0">
            <SheetTitle className="flex items-center gap-2 text-[13px]">
              <div className="h-6 w-6 rounded-md bg-primary/10 flex items-center justify-center">
                <Info className="h-3.5 w-3.5 text-primary" />
              </div>
              Why is this the current state?
            </SheetTitle>
            <SheetDescription className="text-[11px] text-muted-foreground/50">
              Resolution explanation for this capability
            </SheetDescription>
          </SheetHeader>
          {whyData && (
            <div className="mt-5 space-y-4">
              {/* Capability header */}
              <div className="rounded-lg bg-muted/10 border border-border/30 p-3.5">
                <p className="text-[13px] font-semibold text-foreground">{whyData.cap.label}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant="outline" className={`text-[9px] font-normal ${MODE_COLORS[whyData.cap.effectiveMode]}`}>
                    {whyData.cap.effectiveMode.replace("_", " ")}
                  </Badge>
                  <Badge variant="outline" className={`text-[9px] font-normal ${whyData.cap.enabled ? "bg-success/8 text-success/70 border-success/15" : "bg-destructive/8 text-destructive/70 border-destructive/15"}`}>
                    {whyData.cap.enabled ? "Enabled" : "Disabled"}
                  </Badge>
                  {!whyData.cap.accessible && (
                    <Badge variant="outline" className="text-[9px] text-destructive/60 border-destructive/15 font-normal">
                      Not Accessible
                    </Badge>
                  )}
                </div>
              </div>

              {/* Plain language explanation */}
              <div className="rounded-lg bg-primary/4 border border-primary/10 p-3.5">
                <p className="text-[11px] text-foreground/80 leading-relaxed">{whyData.summary.explanation}</p>
              </div>

              {/* Resolution factors */}
              <div className="space-y-2.5">
                <p className="text-[10px] text-muted-foreground/50 uppercase tracking-wider">Resolution Factors</p>

                <div className="grid grid-cols-2 gap-2">
                  <div className="p-2.5 rounded-lg bg-muted/10 border border-border/20">
                    <p className="text-[9px] text-muted-foreground/40 mb-0.5">Final State</p>
                    <p className="text-[11px] font-semibold text-foreground capitalize">{whyData.summary.finalState.replace("_", " ")}</p>
                  </div>
                  <div className="p-2.5 rounded-lg bg-muted/10 border border-border/20">
                    <p className="text-[9px] text-muted-foreground/40 mb-0.5">Deciding Pass</p>
                    <p className="text-[11px] font-semibold text-foreground">{whyData.summary.primaryDecidingPass}</p>
                  </div>
                </div>

                {whyData.summary.overriddenBy && (
                  <div className="flex items-center gap-2 text-[11px] p-2.5 rounded-lg bg-warning/6 border border-warning/12">
                    <Zap className="h-3.5 w-3.5 text-warning/60 shrink-0" />
                    <span className="text-warning/80">Overridden by: {whyData.summary.overriddenBy}</span>
                  </div>
                )}

                {whyData.summary.roleRestriction && (
                  <div className="flex items-center gap-2 text-[11px] p-2.5 rounded-lg bg-destructive/6 border border-destructive/12">
                    <ShieldAlert className="h-3.5 w-3.5 text-destructive/60 shrink-0" />
                    <span className="text-destructive/80">{whyData.summary.roleRestriction}</span>
                  </div>
                )}

                {whyData.summary.tenantInfluence && (
                  <div className="flex items-center gap-2 text-[11px] p-2.5 rounded-lg bg-primary/6 border border-primary/12">
                    <Building2 className="h-3.5 w-3.5 text-primary/60 shrink-0" />
                    <span className="text-primary/80">Tenant: {whyData.summary.tenantInfluence}</span>
                  </div>
                )}

                {whyData.summary.dependencyIssues.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-[10px] text-muted-foreground/50 uppercase tracking-wider">Dependency Issues</p>
                    {whyData.summary.dependencyIssues.map((d, i) => (
                      <p key={i} className="text-[11px] text-destructive/70 pl-3 border-l-2 border-destructive/15">{d}</p>
                    ))}
                  </div>
                )}

                {whyData.summary.conflictIssues.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-[10px] text-muted-foreground/50 uppercase tracking-wider">Conflict Issues</p>
                    {whyData.summary.conflictIssues.map((d, i) => (
                      <p key={i} className="text-[11px] text-destructive/70 pl-3 border-l-2 border-destructive/15">{d}</p>
                    ))}
                  </div>
                )}
              </div>

              <Separator className="bg-border/20" />

              {/* Override stack */}
              <div className="space-y-2">
                <p className="text-[10px] text-muted-foreground/50 uppercase tracking-wider">Override Stack</p>
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-[11px] p-2 rounded-md bg-muted/8">
                    <span className="text-muted-foreground/60">Role Default</span>
                    <SourceBadge source="role" />
                  </div>
                  <div className="flex items-center justify-between text-[11px] p-2 rounded-md bg-muted/8">
                    <span className="text-muted-foreground/60">Tenant Override</span>
                    {whyData.tenantOverride ? (
                      <Badge variant="outline" className="text-[9px] bg-primary/6 text-primary/70 border-primary/15 font-normal">
                        {whyData.tenantOverride.behaviorMode.replace("_", " ")}
                      </Badge>
                    ) : <span className="text-[10px] text-muted-foreground/20">None</span>}
                  </div>
                  <div className="flex items-center justify-between text-[11px] p-2 rounded-md bg-muted/8">
                    <span className="text-muted-foreground/60">User Override</span>
                    {whyData.userOverride ? (
                      <Badge variant="outline" className="text-[9px] bg-warning/8 text-warning/70 border-warning/15 font-semibold">
                        {whyData.userOverride.behaviorMode.replace("_", " ")}
                      </Badge>
                    ) : <span className="text-[10px] text-muted-foreground/20">None</span>}
                  </div>
                </div>
              </div>

              {/* Decision timeline */}
              {whyData.cap.passTrace.length > 0 && (
                <div className="space-y-2">
                  <p className="text-[10px] text-muted-foreground/50 uppercase tracking-wider">Decision Timeline</p>
                  <div className="space-y-1">
                    {whyData.cap.passTrace.map((t, i) => (
                      <div key={i} className="flex items-center gap-2 text-[10px] p-2 rounded-md bg-muted/8">
                        <div className="h-4 w-4 rounded-full bg-primary/8 flex items-center justify-center text-[8px] font-semibold text-primary/60 shrink-0">{i + 1}</div>
                        <Badge variant="outline" className="text-[8px] shrink-0 font-normal">{t.pass}</Badge>
                        <span className="text-muted-foreground/50 flex-1 truncate">{t.action}</span>
                        <span className={`font-mono text-[9px] ${t.after.enabled ? "text-success/60" : "text-destructive/60"}`}>
                          {t.after.enabled ? "ON" : "OFF"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
